/**
 * useWaveformDataCache
 *
 * Manages a Map<clipId, WaveformData> that stores pre-computed WaveformData
 * objects generated by a web worker. These enable near-instant zoom changes
 * via WaveformData.resample() instead of re-scanning raw audio samples.
 *
 * Behavior:
 * - Creates the worker lazily on first clip needing generation
 * - Generates waveform data once per unique AudioBuffer
 * - Fans out cached/generated waveform data to all clips sharing that buffer
 * - Uses a cancelled flag to ignore state updates after effect cleanup
 * - Terminates the worker on unmount
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { ClipTrack } from '@waveform-playlist/core';
import type WaveformData from 'waveform-data';
import { createPeaksWorker, type PeaksWorkerApi } from '../workers/peaksWorker';

export interface UseWaveformDataCacheReturn {
  cache: ReadonlyMap<string, WaveformData>;
  isGenerating: boolean;
}

export function useWaveformDataCache(
  tracks: ClipTrack[],
  baseScale: number
): UseWaveformDataCacheReturn {
  const [cache, setCache] = useState<Map<string, WaveformData>>(() => new Map());
  const [isGenerating, setIsGenerating] = useState(false);

  const workerRef = useRef<PeaksWorkerApi | null>(null);
  const generatedByBufferRef = useRef<WeakMap<AudioBuffer, WaveformData>>(new WeakMap());
  const inflightByBufferRef = useRef<WeakMap<AudioBuffer, Promise<void>>>(new WeakMap());
  const subscribersByBufferRef = useRef<WeakMap<AudioBuffer, Set<string>>>(new WeakMap());
  const pendingCountRef = useRef(0);

  // Stable callback to get or create the worker.
  // Also replaces a crashed worker (onerror marks it terminated) — reusing
  // it would reject every later generation until the component remounts.
  const getWorker = useCallback(() => {
    if (!workerRef.current || workerRef.current.isTerminated()) {
      workerRef.current = createPeaksWorker();
    }
    return workerRef.current;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const generatedByBuffer = generatedByBufferRef.current;
    const inflightByBuffer = inflightByBufferRef.current;
    const subscribersByBuffer = subscribersByBufferRef.current;
    const immediateAssignments: Array<{ clipId: string; waveformData: WaveformData }> = [];
    const buffersToProcess = new Map<AudioBuffer, Set<string>>();

    for (const track of tracks) {
      for (const clip of track.clips) {
        if (!clip.audioBuffer || clip.waveformData) {
          continue;
        }

        const existingWaveformData = generatedByBuffer.get(clip.audioBuffer);
        if (existingWaveformData) {
          immediateAssignments.push({
            clipId: clip.id,
            waveformData: existingWaveformData,
          });
          continue;
        }

        if (inflightByBuffer.get(clip.audioBuffer)) {
          const existingSubscribers = subscribersByBuffer.get(clip.audioBuffer);
          if (existingSubscribers) {
            existingSubscribers.add(clip.id);
          } else {
            subscribersByBuffer.set(clip.audioBuffer, new Set([clip.id]));
          }
          continue;
        }

        const clipsForBuffer = buffersToProcess.get(clip.audioBuffer);
        if (clipsForBuffer) {
          clipsForBuffer.add(clip.id);
        } else {
          buffersToProcess.set(clip.audioBuffer, new Set([clip.id]));
        }
      }
    }

    if (immediateAssignments.length > 0) {
      setCache((prev) => {
        const next = new Map(prev);
        for (const assignment of immediateAssignments) {
          next.set(assignment.clipId, assignment.waveformData);
        }
        return next;
      });
    }

    if (buffersToProcess.size === 0) return;

    pendingCountRef.current += buffersToProcess.size;
    setIsGenerating(true);

    const worker = getWorker();

    for (const [audioBuffer, clipIds] of buffersToProcess) {
      subscribersByBuffer.set(audioBuffer, new Set(clipIds));

      // .slice() channel buffers to avoid detaching the original AudioBuffer views
      const channels: ArrayBuffer[] = [];
      for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
        channels.push(audioBuffer.getChannelData(c).slice().buffer as ArrayBuffer);
      }

      const id = `buffer-${Math.random().toString(36).slice(2, 11)}`;
      const job = worker
        .generate({
          id,
          channels,
          length: audioBuffer.length,
          sampleRate: audioBuffer.sampleRate,
          scale: baseScale,
          bits: 16,
          splitChannels: true,
        })
        .then((waveformData) => {
          generatedByBuffer.set(audioBuffer, waveformData);
          const subscribers = subscribersByBuffer.get(audioBuffer) ?? new Set<string>();
          subscribersByBuffer.delete(audioBuffer);
          inflightByBuffer.delete(audioBuffer);
          if (cancelled) return;

          setCache((prev) => {
            const next = new Map(prev);
            for (const clipId of subscribers) {
              next.set(clipId, waveformData);
            }
            return next;
          });

          pendingCountRef.current--;
          if (pendingCountRef.current <= 0) {
            pendingCountRef.current = 0;
            setIsGenerating(false);
          }
        })
        .catch((err) => {
          subscribersByBuffer.delete(audioBuffer);
          inflightByBuffer.delete(audioBuffer);
          if (cancelled) return;
          console.warn('[waveform-playlist] Worker peak generation failed:', err);
          pendingCountRef.current--;
          if (pendingCountRef.current <= 0) {
            pendingCountRef.current = 0;
            setIsGenerating(false);
          }
        });

      inflightByBuffer.set(audioBuffer, job);
    }

    return () => {
      cancelled = true;
      inflightByBufferRef.current = new WeakMap();
      subscribersByBufferRef.current = new WeakMap();
      pendingCountRef.current = 0;
      setIsGenerating(false);
    };
  }, [tracks, baseScale, getWorker]);

  // Terminate worker on unmount
  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  return { cache, isGenerating };
}
